"use client";

/**
 * The colour worlds in motion: each photo arrives the way it looks (the
 * runner runs in, the wrist is found by a spotlight, the charge photo fills
 * like a battery, the speaker thumps), giant words rise and drift, titles
 * come up word by word and the products follow.
 *
 * The first screen animates in pure CSS on load (colour-worlds.module.css),
 * so it never waits for this script; everything below is driven here, on the
 * `data-world` / `data-fx` hooks in ColourWorldsHome. Motion only plays with
 * prefers-reduced-motion: no-preference; without this script (or with reduced
 * motion) the page is simply still.
 */

import { useEffect, useRef, type ReactNode } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

export default function Motion({ className, children }: { className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    ScrollTrigger.config({ ignoreMobileResize: true });

    const all = (sel: string, el: ParentNode = root): HTMLElement[] => Array.from(el.querySelectorAll<HTMLElement>(sel));
    const one = (sel: string, el: ParentNode = root): HTMLElement | null => el.querySelector<HTMLElement>(sel);
    const vw = () => window.innerWidth / 100;

    const mm = gsap.matchMedia();
    mm.add({ motion: "(prefers-reduced-motion: no-preference)", wide: "(min-width: 900px)" }, (context) => {
      const { motion, wide } = context.conditions as { motion: boolean; wide: boolean };
      if (!motion) return;

      for (const sec of all("[data-world]")) {
        const id = sec.dataset.world;
        const dir = sec.dataset.flip === "1" ? -1 : 1; // which side the photo sits on (desktop spreads)
        const area = one("[data-fx=photo]", sec);
        const img = area?.querySelector("img") ?? null;

        // ── The first screen: depth as it scrolls away (its entrance is CSS) ──
        if (id === "sound") {
          const st = { trigger: sec, start: "top top", end: "bottom top", scrub: true, invalidateOnRefresh: true };
          const giant = one("[data-fx=giant]", sec);
          const copy = one("[data-fx=copy]", sec);
          if (giant) gsap.to(giant, { yPercent: -38, opacity: 0.3, ease: "none", scrollTrigger: st });
          if (area) gsap.to(area, { y: () => sec.offsetHeight * 0.1, scale: 1.05, ease: "none", scrollTrigger: st });
          if (copy) gsap.to(copy, { y: () => -sec.offsetHeight * 0.06, ease: "none", scrollTrigger: st });
          continue;
        }

        // ── Giant words: rise into place, then drift with the scroll ──
        for (const g of all("[data-fx=giant]", sec)) {
          const from = g.dataset.from; // "left" / "right" (FAST, CHARGE) or "grow" (LOUD)
          gsap.fromTo(
            g,
            {
              yPercent: from === "left" || from === "right" ? 0 : 42,
              xPercent: from === "left" ? -70 : from === "right" ? 70 : 0,
              scale: from === "grow" ? 0.72 : from ? 1 : 1.1,
              opacity: 0,
            },
            { yPercent: 0, xPercent: 0, scale: 1, opacity: 1, ease: "power2.out", scrollTrigger: { trigger: sec, start: "top 92%", end: "top 38%", scrub: 0.6 } }
          );
          gsap.fromTo(
            g,
            { x: () => -dir * 1.6 * vw() },
            { x: () => dir * 1.6 * vw(), ease: "none", scrollTrigger: { trigger: sec, start: "top bottom", end: "bottom top", scrub: true, invalidateOnRefresh: true } }
          );
        }

        // ── Depth inside every photo frame ──
        if (img && id !== "visit") {
          gsap.fromTo(img, { yPercent: -5, scale: 1.1 }, { yPercent: 5, scale: 1.1, ease: "none", scrollTrigger: { trigger: area, start: "top bottom", end: "bottom top", scrub: true } });
        }

        // ── Each photo arrives the way it looks ──
        if (area) {
          switch (id) {
            case "buds": {
              // She glides in from her side of the spread.
              gsap.fromTo(
                area,
                { x: () => (wide ? dir : 1) * 15 * vw(), scale: 1.05, opacity: 0.15 },
                { x: 0, scale: 1, opacity: 1, ease: "power2.out", scrollTrigger: { trigger: sec, start: "top 88%", end: "top 25%", scrub: 0.7, invalidateOnRefresh: true } }
              );
              break;
            }
            case "runner": {
              // She runs in from the right (the way she faces) and keeps going:
              // the frame runs in, then the picture drifts on inside it (so no
              // edge of the frame ever shows).
              const tl = gsap.timeline({ scrollTrigger: { trigger: area, start: "top bottom", end: "bottom top", scrub: 0.7, invalidateOnRefresh: true } });
              tl.fromTo(area, { x: () => 58 * vw(), skewX: -8 }, { x: 0, skewX: 0, ease: "power2.out", duration: 0.45 });
              if (img) tl.fromTo(img, { xPercent: 0 }, { xPercent: -4, ease: "none", duration: 0.55 });
              break;
            }
            case "premium": {
              // A spotlight finds the wrist; the cyan light swells.
              gsap.fromTo(
                area,
                { clipPath: "circle(10% at 55% 45%)" },
                { clipPath: "circle(110% at 55% 45%)", ease: "power1.inOut", scrollTrigger: { trigger: sec, start: "top 85%", end: "top 15%", scrub: 0.6 } }
              );
              const glow = one("[data-fx=glow]", sec);
              if (glow) gsap.fromTo(glow, { scale: 0.5, opacity: 0 }, { scale: 1.15, opacity: 0.55, ease: "none", scrollTrigger: { trigger: sec, start: "top 85%", end: "center center", scrub: true } });
              break;
            }
            case "power": {
              // Pops in charged, a shine runs across.
              const tl = gsap.timeline({ scrollTrigger: { trigger: area, start: "top 80%", once: true } });
              tl.from(area, { scale: 0.84, y: 50, opacity: 0, duration: 1.1, ease: "back.out(1.5)" });
              const sheen = one("[data-fx=sheen]", area);
              if (sheen) tl.fromTo(sheen, { xPercent: -130, skewX: -14, opacity: 1 }, { xPercent: 300, duration: 1.1, ease: "power2.inOut" }, "-=0.5");
              break;
            }
            case "charge": {
              // The tall photo fills from the bottom, like a battery.
              const r = wide ? 28 : 0;
              gsap.fromTo(
                area,
                { clipPath: `inset(100% 0% 0% 0% round ${r}px)` },
                { clipPath: `inset(0% 0% 0% 0% round ${r}px)`, ease: "power2.out", scrollTrigger: { trigger: sec, start: "top 80%", end: "top 20%", scrub: 0.6 } }
              );
              break;
            }
            case "loud": {
              // The room comes into focus, then the bass thumps twice.
              gsap.fromTo(area, { scale: 1.25 }, { scale: 1, ease: "none", scrollTrigger: { trigger: sec, start: "top bottom", end: "top 15%", scrub: 0.6 } });
              const frame = one("[data-photo]", area);
              if (frame) {
                gsap
                  .timeline({ scrollTrigger: { trigger: area, start: "top 40%", once: true } })
                  .to(frame, { scale: 1.035, duration: 0.09, ease: "power2.out" })
                  .to(frame, { scale: 1, duration: 0.26, ease: "power3.out" })
                  .to(frame, { scale: 1.02, duration: 0.08, ease: "power2.out" }, "+=0.1")
                  .to(frame, { scale: 1, duration: 0.32, ease: "power3.out" });
              }
              break;
            }
            case "create": {
              // The creator's frame tilts up like a raised phone.
              const card = one("[data-fx=card]", sec) ?? area;
              gsap.fromTo(
                card,
                { rotateX: 26, y: 90, scale: 0.9, transformPerspective: 1100, transformOrigin: "50% 100%" },
                { rotateX: 0, y: 0, scale: 1, ease: "power2.out", scrollTrigger: { trigger: sec, start: "top 85%", end: "top 25%", scrub: 0.7 } }
              );
              break;
            }
            case "play": {
              // Spins in, playful.
              gsap.from(area, { rotate: -8, scale: 0.85, opacity: 0, duration: 1.2, ease: "back.out(1.6)", scrollTrigger: { trigger: area, start: "top 80%", once: true } });
              break;
            }
            case "visit": {
              // A slow push-in on the portrait; the store card rises.
              if (img) gsap.fromTo(img, { scale: 1.3 }, { scale: 1, ease: "none", scrollTrigger: { trigger: sec, start: "top bottom", end: "bottom bottom", scrub: true } });
              const panel = one("[data-fx=panel]", sec);
              if (panel) gsap.from(panel, { y: 70, opacity: 0, duration: 1.1, ease: "expo.out", scrollTrigger: { trigger: panel, start: "top 95%", once: true } });
              break;
            }
          }
        }

        // ── Words: titles rise word by word, the small print follows ──
        for (const words of all("[data-fx=words]", sec)) {
          gsap.from(all("[data-w] > span", words), { yPercent: 115, duration: 1.05, ease: "expo.out", stagger: 0.07, scrollTrigger: { trigger: words, start: "top 88%", once: true } });
        }
        const ups = all("[data-fx=up]", sec);
        if (ups.length) {
          gsap.from(ups, { y: 26, opacity: 0, duration: 0.9, ease: "power3.out", stagger: 0.08, scrollTrigger: { trigger: ups[0], start: "top 90%", once: true } });
        }

        // ── Products glide up one after another (game cards flip in) ──
        for (const rail of all("[data-fx=rail]", sec)) {
          const items = Array.from(rail.children) as HTMLElement[];
          if (!items.length) continue;
          const flip = rail.dataset.style === "flip";
          gsap.from(items, {
            ...(flip ? { rotateY: 70, transformPerspective: 900, ease: "back.out(1.4)" } : { y: 56, scale: 0.94, ease: "expo.out" }),
            opacity: 0,
            duration: 1,
            stagger: 0.07,
            scrollTrigger: { trigger: rail, start: "top 92%", once: true },
          });
        }
      }
    });

    // Photos and fonts that arrive late can move things: measure again.
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener("load", refresh);
    document.fonts?.ready.then(refresh).catch(() => {});

    return () => {
      window.removeEventListener("load", refresh);
      mm.revert();
    };
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
